import React, { useEffect, useRef, useState } from "react";
import { Modal, Tabs, Tooltip, Button, Spin, message } from "antd";
import {
  EyeOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  DownloadOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
} from "@ant-design/icons";
import {
  downloadQueryDocument,
  fetchQueryDocumentPreviewBlob,
  fetchQueryDocumentViewUrl,
} from "../../../../apis/hmoQueryService";
import { downloadDocument as downloadBpaDocument } from "../../../../apis/documentService";
import {
  findQueryDocumentTabKey,
  isUsableQueryDocumentViewUrl,
  resolveQueryUploadPreviewUrl,
} from "../../../../utils/queryDocumentUtils";

const { TabPane } = Tabs;

type Document = {
  documentUrl: string;
  documentType: string;
  documentLabelType: string;
  documentName?: string;
  id?: number;
  viewUrl?: string;
};

type DocumentPreviewModalProps = {
  open: boolean;
  handleCancel: () => void;
  documents: Document[];
  handleDownloadDocument: (fileName: string | undefined, url: string) => void;
  setDocumentName?: (name: string) => void;
  clickedActiveTab?: string;
  /** @deprecated deficiency review removed from query document preview */
  handleDeficiencyModal?: unknown;
  deficiencyQuestions?: unknown;
  selectedDocument?: unknown;
  activeKey?: unknown;
  queryStageId?: unknown;
  viewedQuery?: unknown;
};

const ViewQueryUploadedDocument: React.FC<DocumentPreviewModalProps> = ({
  open,
  handleCancel,
  documents,
  handleDownloadDocument,
  setDocumentName,
  clickedActiveTab,
}) => {
  const [activeDocument, setActiveDocument] = useState<Document | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [documentUrls, setDocumentUrls] = useState<Document[]>([]);
  const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoadingId, setDownloadLoadingId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>();
  const initialSelectionRef = useRef(false);
  const blobPreviewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (documents) {
      setDocumentUrls(documents);
    }
  }, [documents]);

  const groupedDocuments: Record<string, Document[]> = documentUrls.reduce(
    (acc, doc) => {
      if (doc.documentUrl || doc.id) {
        const tabKey = doc.documentLabelType || doc.documentType || "Document";
        (acc[tabKey] ||= []).push(doc);
      }
      return acc;
    },
    {} as Record<string, Document[]>,
  );

  useEffect(() => {
    if (clickedActiveTab) {
      setActiveTab(
        findQueryDocumentTabKey(documentUrls, clickedActiveTab) ||
          clickedActiveTab,
      );
    } else if (!activeTab) {
      setActiveTab(Object.entries(groupedDocuments)?.[0]?.[0]);
    }
  }, [clickedActiveTab, open, documentUrls, groupedDocuments, activeTab]);

  const previewSource =
    resolvedPreviewUrl ||
    (activeDocument ? resolveQueryUploadPreviewUrl(activeDocument) : null);

  const isPdf =
    Boolean(previewSource?.toLowerCase().includes(".pdf")) ||
    Boolean(activeDocument?.documentName?.toLowerCase().endsWith(".pdf"));

  useEffect(() => {
    let cancelled = false;

    const clearBlobPreview = () => {
      if (blobPreviewUrlRef.current) {
        URL.revokeObjectURL(blobPreviewUrlRef.current);
        blobPreviewUrlRef.current = null;
      }
    };

    const resolvePreview = async () => {
      if (!open || !activeDocument) {
        clearBlobPreview();
        setResolvedPreviewUrl(null);
        setPreviewLoading(false);
        return;
      }

      const cached = resolveQueryUploadPreviewUrl(activeDocument);
      if (cached) {
        clearBlobPreview();
        setResolvedPreviewUrl(cached);
        setPreviewLoading(false);
        return;
      }

      if (!activeDocument.id) {
        clearBlobPreview();
        setResolvedPreviewUrl(null);
        setPreviewLoading(false);
        return;
      }

      setPreviewLoading(true);
      try {
        const response = await fetchQueryDocumentViewUrl(activeDocument.id);
        if (cancelled) return;
        if (isUsableQueryDocumentViewUrl(response.viewUrl)) {
          clearBlobPreview();
          setResolvedPreviewUrl(response.viewUrl || null);
          return;
        }

        const preview = await fetchQueryDocumentPreviewBlob(activeDocument.id);
        if (cancelled) {
          URL.revokeObjectURL(preview.blobUrl);
          return;
        }
        clearBlobPreview();
        blobPreviewUrlRef.current = preview.blobUrl;
        setResolvedPreviewUrl(preview.blobUrl);
      } catch {
        if (!cancelled && activeDocument.id) {
          try {
            const preview = await fetchQueryDocumentPreviewBlob(activeDocument.id);
            if (cancelled) {
              URL.revokeObjectURL(preview.blobUrl);
              return;
            }
            clearBlobPreview();
            blobPreviewUrlRef.current = preview.blobUrl;
            setResolvedPreviewUrl(preview.blobUrl);
            return;
          } catch {
            // handled below
          }
        }
        if (!cancelled) {
          clearBlobPreview();
          setResolvedPreviewUrl(null);
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };

    resolvePreview().catch(() => undefined);

    return () => {
      cancelled = true;
      if (blobPreviewUrlRef.current) {
        URL.revokeObjectURL(blobPreviewUrlRef.current);
        blobPreviewUrlRef.current = null;
      }
    };
  }, [activeDocument, open]);

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));
  const handleRotateLeft = () => setRotationAngle((prev) => prev - 90);
  const handleRotateRight = () => setRotationAngle((prev) => prev + 90);

  const downloadBlobUrl = (blobUrl: string, fileName?: string) => {
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName || "document";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const handleDownload = async (doc: Document) => {
    const fileName = doc.documentName || doc.documentLabelType || "document";
    setDownloadLoadingId(doc.id ?? null);

    try {
      if (doc.id) {
        try {
          await downloadQueryDocument(doc.id, fileName);
          return;
        } catch {
          await downloadBpaDocument(doc.id, fileName);
          return;
        }
      }

      const localUrl = resolveQueryUploadPreviewUrl(doc);
      if (localUrl?.startsWith("blob:")) {
        downloadBlobUrl(localUrl, fileName);
        return;
      }

      if (localUrl && isUsableQueryDocumentViewUrl(localUrl)) {
        const response = await fetch(localUrl);
        if (!response.ok) {
          throw new Error("Unable to download document");
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        try {
          downloadBlobUrl(blobUrl, fileName);
        } finally {
          window.URL.revokeObjectURL(blobUrl);
        }
        return;
      }

      if (doc.documentUrl) {
        await handleDownloadDocument(fileName, doc.documentUrl);
        return;
      }

      message.error("Unable to download document.");
    } catch {
      message.error("Unable to download document.");
    } finally {
      setDownloadLoadingId(null);
    }
  };

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
    setResolvedPreviewUrl(null);
  }, [activeDocument]);

  useEffect(() => {
    setActiveDocument(null);
  }, [activeTab]);

  const selectDocument = (doc: Document) => {
    setActiveDocument(doc);
    if (doc.documentName) {
      setDocumentName?.(doc.documentName);
    }
  };

  useEffect(() => {
    if (
      open &&
      Object.keys(groupedDocuments).length > 0 &&
      activeTab &&
      !activeDocument
    ) {
      const doc = groupedDocuments[activeTab]?.[0];
      if (doc) {
        selectDocument(doc);
      }
    }
  }, [groupedDocuments, open, activeTab, activeDocument]);

  useEffect(() => {
    if (
      open &&
      !initialSelectionRef.current &&
      Object.keys(groupedDocuments).length > 0 &&
      activeTab
    ) {
      const doc = groupedDocuments[activeTab]?.[0];
      if (doc) {
        selectDocument(doc);
      }
      initialSelectionRef.current = true;
    }
  }, [groupedDocuments, open, activeTab]);

  useEffect(() => {
    if (!open) {
      initialSelectionRef.current = false;
      setActiveDocument(null);
    }
  }, [open]);

  return (
    <div className="patient-details-stage-modal">
      <Modal
        open={open}
        onCancel={handleCancel}
        width={1200}
        style={{ top: 20 }}
        wrapClassName="patient-details-stage-modal"
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: isPdf ? "flex-end" : "space-between",
            }}
          >
            {!isPdf && activeDocument && (
              <div>
                <Button onClick={handleZoomOut} disabled={zoomLevel <= 0.5}>
                  <ZoomOutOutlined />
                </Button>
                <Button
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 3}
                  style={{ marginLeft: 8 }}
                >
                  <ZoomInOutlined />
                </Button>
                <Button onClick={handleRotateLeft} style={{ marginLeft: 8 }}>
                  <RotateLeftOutlined />
                </Button>
                <Button onClick={handleRotateRight} style={{ marginLeft: 8 }}>
                  <RotateRightOutlined />
                </Button>
              </div>
            )}
            <Button onClick={handleCancel}>Close</Button>
          </div>
        }
      >
        <Tabs
          type="card"
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
        >
          {Object.entries(groupedDocuments).map(([docType, docs]) => (
            <TabPane tab={docType} key={docType}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                {docs.map((doc, index) => {
                  const isActive =
                    activeDocument?.documentUrl === doc.documentUrl &&
                    activeDocument?.id === doc.id;
                  return (
                    <div
                      key={`${docType}-${doc.id ?? index}`}
                      style={{
                        border: isActive ? "2px solid #005499" : "1px solid #ccc",
                        borderRadius: "8px",
                        padding: "2px 8px",
                        width: 250,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: isActive ? "#e6f7ff" : "#fff",
                      }}
                    >
                      <Tooltip title={doc.documentLabelType || doc.documentName}>
                        <div style={{ fontWeight: 500 }}>
                          {(doc.documentLabelType || doc.documentName)?.slice(
                            0,
                            18,
                          ) || `Document ${index + 1}`}
                        </div>
                      </Tooltip>
                      <div style={{ display: "flex", gap: 10 }}>
                        <Tooltip title="View">
                          <EyeOutlined
                            style={{ color: "#008B8B", cursor: "pointer" }}
                            onClick={() => selectDocument(doc)}
                          />
                        </Tooltip>
                        <Tooltip title="Download">
                          {downloadLoadingId === doc.id ? (
                            <Spin size="small" />
                          ) : (
                            <DownloadOutlined
                              style={{ color: "#008B8B", cursor: "pointer" }}
                              onClick={() => handleDownload(doc)}
                            />
                          )}
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabPane>
          ))}
        </Tabs>

        {activeDocument && (
          <div style={{ marginTop: "1rem" }}>
            <h3 style={{ marginBottom: 12 }}>
              {activeDocument.documentName ||
                activeDocument.documentLabelType ||
                "Document preview"}
            </h3>
            {previewLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "48px 0",
                }}
              >
                <Spin />
              </div>
            ) : previewSource ? (
              isPdf ? (
                <iframe
                  src={previewSource}
                  title="PDF Preview"
                  width="100%"
                  height="600px"
                  style={{ border: "1px solid #e5e7eb", borderRadius: 8 }}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    overflow: "auto",
                    height: 600,
                  }}
                >
                  <img
                    src={previewSource}
                    alt="Preview"
                    style={{
                      transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                      transition: "transform 0.3s ease",
                      maxWidth: "none",
                      height: "auto",
                    }}
                  />
                </div>
              )
            ) : (
              <div style={{ color: "#6b7280", padding: "24px 0" }}>
                Unable to load document preview.
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ViewQueryUploadedDocument;
